import React, {useState} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {ManualSelectBase} from 'app/components/general/SelectBase/SelectBase';
import {useCurrentUserState} from 'app/current-user/api';
import {createFormErrorsContext, FormControlGroup} from 'app/general/forms';
import {KBTreeEditItemStructure} from 'app/kbs/components/KBEditPage/KBTree/KBTreeEditItemStructure';
import {SearchItemsSelectMulti} from 'app/search/components/SearchItemsSelect';
import {useCreateTeam} from 'app/teams/api';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {BasicTeamSerializedType} from 'sweet-types';

import type {ActionMeta} from 'app/components/general/SweetReactSelect';
import type {FormErrorsProps} from 'app/general/forms';
import type {SearchItemType} from 'app/search/types';

export interface InitialProps {
  name: string;
  teams: Array<BasicTeamSerializedType>;
}

export interface DoChangeProps {
  name: string;
  teams: Array<SearchItemType>;
}

export interface OwnChangeProps {
  doChange: (changeProps: DoChangeProps) => Promise<any>;
  onTeamChange: (
    teams: Array<SearchItemType>,
    action: ActionMeta<SearchItemType>
  ) => Promise<any>;
  onCancel: () => void;
  initial: InitialProps;
  allowedTeams: Array<BasicTeamSerializedType>;
  isManual: boolean;
  autoFocus?: boolean;
  inPopover: boolean;
}

type Props = FormErrorsProps & OwnChangeProps;

export const ChangeFolder = createFormErrorsContext<Props>(
  ({
    doChange,
    onTeamChange,
    onCancel,
    initial,
    allowedTeams,
    isManual,
    autoFocus,
    captureFormErrorsFromRejection,
    inPopover,
  }: Props) => {
    const {is_super_manager} = useCurrentUserState().data!;
    const [createTeam] = useCreateTeam();

    const [name, setName] = useState<string>(initial.name);
    const [selected, setSelected] = useState<Array<SearchItemType>>(
      initial.teams.map(makeSearchTeamSimple)
    );

    const options: Array<SearchItemType> =
      allowedTeams.map(makeSearchTeamSimple);

    const onSubmit = (): Promise<any> => {
      if (name !== initial.name) {
        return doChange({
          name,
          teams: selected,
        }).catch(captureFormErrorsFromRejection);
      } else {
        return Promise.resolve(onCancel());
      }
    };

    // on changing a team.
    const onChange = (
      teams: Array<SearchItemType>,
      action: ActionMeta<SearchItemType>
    ) => onTeamChange(teams, action).then(() => setSelected(teams));

    const StyledDiv =
      inPopover === true
        ? StyledFormattingDivVertical
        : StyledFormattingDivHorizontal;

    return (
      <FormControlGroup field="name" className="flex-grow-1 mb-0">
        {(invalidFeedback, errorClass) => (
          <>
            <KBTreeEditItemStructure
              onSubmit={onSubmit}
              onCancel={onCancel}
              disabled={selected.length < 1}
              className={
                inPopover
                  ? 'justify-content-center col-12 mb-2'
                  : 'align-items-center '
              }
              inPopover={inPopover}
            >
              {(saving) => (
                <StyledDiv>
                  <input
                    autoFocus={autoFocus}
                    className={cs('form-control', errorClass, {
                      'mb-2': inPopover,
                    })}
                    type="text"
                    readOnly={saving}
                    name="name"
                    placeholder={gt('Folder name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />

                  {isManual ? (
                    <ManualSelectBase
                      isMulti={true}
                      disabled={options.length === 0}
                      selected={selected}
                      placeholder={
                        options.length > 0
                          ? gt('Select a team to assign to.')
                          : gt('Assign a team to folder one level up first.')
                      }
                      getOptionValue={(obj) => obj.object_id}
                      options={options}
                      noOptionsMessage={() =>
                        gt('Folder one level up does not have any teams.')
                      }
                      typeIcon={(obj) => getContentType(obj.content_type).icon}
                      onChange={onChange}
                    />
                  ) : (
                    <SearchItemsSelectMulti
                      disabled={saving}
                      selected={selected}
                      onChange={onChange}
                      lazyFetch={false}
                      params={{
                        ct: 'team',
                        ordering: 'name',
                      }}
                      placeholder={
                        is_super_manager
                          ? gt('Select or create a team to assign to')
                          : gt('Select a team to assign to')
                      }
                      creatable={is_super_manager}
                      noOptionsMessage={() =>
                        is_super_manager
                          ? gt(
                              'No teams found. Enter the team name to create...'
                            )
                          : gt('No teams found.')
                      }
                      selectProps={{
                        isClearable: false,
                        isOptionDisabled: () => false,
                        getOptionValue: (obj) => obj.object_id,
                        formatCreateLabel: (name) =>
                          gt('Create "%s" team', name),
                        onCreateOption: (name) =>
                          createTeam({name})
                            .unwrap()
                            .then(makeSearchTeamSimple)
                            .then((simpleteam) =>
                              onChange([...selected, simpleteam], {
                                action: 'select-option',
                                option: simpleteam,
                              })
                            ),
                      }}
                    />
                  )}
                </StyledDiv>
              )}
            </KBTreeEditItemStructure>
            {invalidFeedback}
          </>
        )}
      </FormControlGroup>
    );
  }
);

const makeSearchTeamSimple = (
  team: BasicTeamSerializedType
): SearchItemType => ({
  id: team.id, // Need id for SelectBaseRTK
  object_id: team.id,
  content_type: 'team',
  name: team.name,
  html_url: '',
  highlight: '',
});

const StyledFormattingDivVertical = styled.div.attrs({
  className: 'col-12 mb-2',
})``;

const StyledFormattingDivHorizontal = styled.div`
  display: flex;
  flex-grow: 1;
  flex-wrap: nowrap;

  & > * {
    flex-grow: 1;

    &:last-child > div {
      border-radius: 0;
    }
  }

  @media (min-width: 576px) {
    & > * {
      flex-basis: 50%;

      &:first-child {
        border-right: none;
        border-radius: 0.25rem 0 0 0.25rem;
      }
    }
  }

  @media (max-width: 575px) {
    flex-wrap: wrap;
    & > * {
      flex-basis: 100%;

      &:first-child {
        border-radius: 0.25rem 0.25rem 0 0;
        border-bottom: none;
      }
    }
  }
`;
